/**
 * ActivityTableView (Story 34.5 — restyled to match the clients table).
 *
 * Renders audit events in the SAME table shell as ClientTableView: a rounded-[22px]
 * muted card with a 0.5px border, bg-background header + rows, a subtle hover tint,
 * and whole-row click that opens the detail modal. Read-only — no row action menu.
 *
 * Columns mirror the client table's rhythm: Actor (avatar + email) · Action
 * (humanized sentence) · Resource · When (timestamp).
 */

import { cn } from "@provenleap/ui";
import { ActorAvatar } from "./ActorAvatar";
import { HumanizedAction } from "./HumanizedAction";
import { TableHeaderIcon } from "../shared/TableHeaderIcon";
import type { AuditEventDto } from "../../hooks/useAuditEvents";

/** "report_template" → "Report template". */
function prettyResource(resourceType: string): string {
  const words = resourceType.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function OutcomeChip({ outcome }: { outcome: string }) {
  const isFailure = outcome === "failure" || outcome === "error";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium capitalize shrink-0",
        isFailure
          ? "bg-destructive/10 text-destructive"
          : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
      )}
    >
      {outcome}
    </span>
  );
}

export function ActivityTableView({
  events,
  onSelect,
}: {
  events: AuditEventDto[];
  onSelect: (event: AuditEventDto) => void;
}) {
  return (
    <div
      className="overflow-hidden rounded-[22px] bg-muted/50 text-foreground shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <table className="app-page-table w-full text-sm" aria-label="Activity log">
        <thead className="relative bg-background">
          <tr>
            <th scope="col" className="text-left py-2 px-4 text-[13px] font-semibold text-foreground">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="actor" />
                Actor
              </span>
            </th>
            <th scope="col" className="text-left py-2 px-4 text-[13px] font-semibold text-foreground">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="activity" />
                Action
              </span>
            </th>
            <th scope="col" className="text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden md:table-cell whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="resource" />
                Resource
              </span>
            </th>
            <th scope="col" className="text-left py-2 px-4 text-[13px] font-semibold text-foreground hidden sm:table-cell whitespace-nowrap">
              <span className="inline-flex items-center gap-1.5">
                <TableHeaderIcon name="time" />
                When
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => {
            const actor = event.actorEmailSnapshot ?? "System";
            const showOutcome = event.outcome !== "success";
            return (
              <tr
                key={event.id}
                data-testid="activity-row"
                className="bg-background hover:bg-brand/[0.02] transition-colors cursor-pointer"
                onClick={() => onSelect(event)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(event);
                  }
                }}
                tabIndex={0}
              >
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <ActorAvatar email={event.actorEmailSnapshot} />
                    <p className="font-medium truncate">{actor}</p>
                  </div>
                </td>

                <td className="py-3 px-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <HumanizedAction event={event} className="text-[13px] text-muted-foreground truncate" />
                    {showOutcome && <OutcomeChip outcome={event.outcome} />}
                  </div>
                </td>

                <td className="py-3 px-4 hidden md:table-cell whitespace-nowrap">
                  <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[12px] text-foreground/70">
                    {prettyResource(event.resourceType)}
                  </span>
                </td>

                <td className="py-3 px-4 text-foreground whitespace-nowrap hidden sm:table-cell">
                  {new Date(event.occurredAt).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
